import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Winner from "./winner.js";
import OddsTable from "../OddsTable.js";
import Table from "react-bootstrap/Table";
import { styles } from "../../styles/styles.js";
import { EM_DASH } from "../../utils/format.js";
import createScrollSnap from "scroll-snap";

export function Recents({ name, date, league, compact = false }) {
  const [data, setData] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = process.env.REACT_APP_API_URL;
        const { data: response } = await axios.get(`${url}/getFixtures/`, {
          params: {
            team: name,
            date: date,
            league: league,
          },
        });
        setData(response);
        setLoadError(false);
      } catch (error) {
        // Leaving `data` as [] rendered a fully-formed table with headers and
        // zero rows — indistinguishable from "this team has no recent games".
        console.error(error.message);
        setLoadError(true);
      }
    };

    fetchData();
  }, [name, date, league]);

  useEffect(() => {
    if (compact) return;
    const container = containerRef.current;
    if (container) {
      const { bind, unbind } = createScrollSnap(
        container,
        {
          snapDestinationY: "0%",
          timeout: 100,
          duration: 300,
          threshold: 0.2,
          snapStop: false,
          easing: (t) => t * (2 - t),
        },
        () => console.log("Element snapped")
      );

      bind();
      return () => unbind();
    }
  }, [compact]);

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
  };

  // Unit 2 added `loadError` precisely so a failed fetch would stop looking like
  // "no recent games", and then never rendered it -- the state was set on both
  // branches and read nowhere, which `no-unused-vars` had been failing the build
  // over. Saying so is the whole point of having the flag.
  if (loadError) {
    return (
      <div style={styles.text}>
        Recent games could not be loaded.
      </div>
    );
  }

  if (compact) {
    const hitStyle = { ...styles.Cells, color: 'green' };
    const missStyle = { ...styles.Cells, color: '#cc3333' };
    const ouHitStyle = { ...styles.Cells, color: 'white' };
    const ouMissStyle = { ...styles.Cells, color: '#555' };

    return (
      <div>
        <Table bordered className="main">
          <thead>
            <tr>
              <td style={styles.Cells}>Date</td>
              <td style={styles.Cells}>Opp</td>
              <td style={styles.Cells}>Score</td>
              <td style={styles.Cells}>ML</td>
              <td style={styles.Cells}>Spread</td>
              <td style={styles.Cells}>O/U</td>
            </tr>
          </thead>
          <tbody>
            {data.map(fixture => {
              const isHome = fixture.home_team === name;
              const teamPoints = isHome ? fixture.home_points : fixture.away_points;
              const oppPoints = isHome ? fixture.away_points : fixture.home_points;
              const opponent = isHome ? fixture.away_team : fixture.home_team;
              const spread = isHome ? fixture.home_spread : fixture.away_spread;
              const hasScore = teamPoints != null && oppPoints != null;

              // A draw is its own result, not a win. `>=` rendered 'W' for a
              // drawn game, which is the same defect as OddsTable highlighting
              // both teams — one comparison asked to carry three outcomes.
              const drew = hasScore && teamPoints === oppPoints;
              const mlHit = hasScore && teamPoints > oppPoints;
              const hasSpread = spread != null && !Number.isNaN(spread);
              const spreadHit = hasScore && hasSpread && (teamPoints + spread >= oppPoints);
              const total = hasScore ? fixture.home_points + fixture.away_points : null;
              const overHit = hasScore && (total >= fixture.over);
              const underHit = hasScore && (total <= fixture.under);
              return (
                <tr key={fixture.id}>
                  <td style={styles.Cells}>{formatDate(fixture.game_time)}</td>
                  <td style={styles.Cells}>{opponent}</td>
                  <td style={hasScore && !drew ? (mlHit ? hitStyle : missStyle) : styles.Cells}>
                    {hasScore ? `${teamPoints}-${oppPoints}` : EM_DASH}
                  </td>
                  <td style={hasScore && !drew ? (mlHit ? hitStyle : missStyle) : styles.Cells}>
                    {hasScore ? (drew ? 'D' : mlHit ? 'W' : 'L') : EM_DASH}
                  </td>
                  <td style={hasScore && hasSpread ? (spreadHit ? hitStyle : missStyle) : styles.Cells}>
                    {hasSpread ? `${spread > 0 ? '+' : ''}${spread}` : EM_DASH}
                  </td>
                  <td style={hasScore ? (overHit ? ouHitStyle : ouMissStyle) : styles.Cells}>
                    {hasScore ? `${total}` : EM_DASH}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        overflowY: "scroll",
        height: "100vh",
        scrollSnapType: "y mandatory",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
      {Array.from({ length: Math.ceil(data.length / 3) }, (_, index) => (
        <section
          key={index}
          style={{
            minHeight: "100vh",
            scrollSnapAlign: "start",
            marginBottom: "5vh",
          }}
        >
          {data.slice(index * 3, index * 3 + 3).map((fixture) => (
            <div key={fixture.id}>
              <Winner fixture={fixture} name={name} />
              <OddsTable fixture={fixture} selectedTeam={name} mode={"Recents"} />
              <div style={{ height: "10vh" }}></div>
            </div>
          ))}
        </section>
      ))}
      </div>
    </div>
  );
}

export default Recents;
